import {
	Button,
	CancellableEvent,
	error,
	getSubscriptions,
	useIsFeatureEnabled,
	openDrawer,
	success,
	Text,
	WarningDialog,
	NavigationPrompt,
	useCompanyDateTime,
} from '@innovyze/stylovyze';
import { useAuth0 } from '@innovyze/stylovyze/lib/contexts/AuthenticationWrapper';
import { Category, Component, RiskCalcStatusResult, RiskConfig } from '@Types';
import {
	LoFSetup,
	CoFSetup,
	AddNewComponent,
	RiskSetup,
	RiskDetails,
	RiskLogs,
	RenameRisk,
	AddEditComponent,
	RiskDetailsDatagrid,
} from '@Components';
import { FullPageWrapper } from '@Utils/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	selectRiskTab,
	selectLofStore,
	selectCofStore,
	selectRiskStore,
	selectIsRiskSetupDirty,
	selectRiskStatus,
	selectRiskLastRun,
	selectRiskRunning,
	selectDidMatrixUpdate,
	selectRiskConfig,
	selectRiskSetting,
	selectEnablePreview,
	selectUpdatedRiskMatrixGrid,
	selectUpdatedRiskRanges,
	selectOverallRisk,
	selectRiskDetailsQueryObj,
} from '@Selectors';
import {
	saveLoF,
	saveCoF,
	setSaveRun,
	getLog,
	reloadRiskDetails,
	runRisk,
	setRunCompleteReload,
	getRiskConfig,
	getCustomTableFields,
	getProximityAttributeFields,
	getCoFFields,
	getSpatialLayerList,
	getLoFFields,
	getLoFInspectionFields,
	setRiskStatus,
	setRiskLastRun,
	getRiskDetailsDefaulted,
	setRefreshList,
	resetRisk,
	setRiskRunning,
	getSimulationData,
	setDidMatrixUpdate,
	saveRiskConfigAction,
	setRiskSetupDirty,
	setMatrixPreviewStatus,
	getTaskData,
	initRiskMatrixPreview,
	setOnlyGradesUpdated,
	cancelRiskMatrixPreview,
	clearRiskScanId,
	getRiskDetailsResolved,
	setRiskDetailsIsBusy,
	getRiskDetailsRejected,
} from '@Actions';
import RiskCalcStatus from '@Components/RiskCalcStatus/RiskCalcStatus';
import { viewerAccess } from '@innovyze/shared-utils';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { addRouteNamespace } from '@Utils/actions';
import { DialogProps, Grid, IconButton } from '@mui/material';
import { Create } from '@mui/icons-material';
import { selectRiskConfigsPage } from '@innovyze/lib_am_common/Selectors';
import { changeRiskConfigsPage } from '@innovyze/lib_am_common/Actions';
import { useGlobalization } from '@Translations/useGlobalization';
import { noConditionData } from '@Components/RiskDetails/RiskDetails.component';
import {
	formatDateTimeWithFormatDate,
	useWebSockets,
} from '@innovyze/lib_am_common/Utils';
import { InspectionStandardType } from '@Types/InspectionStandardType';
import { WebSocketData } from '@Types/webSocket.types';
import { ResolvedResponse, Row } from '@innovyze/lib_am_common';

export const RiskManagement = ({ page }: { page?: number }) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const params: { configId: string } = useParams();
	const history = useHistory();
	const location = useLocation<{ pageIndex: number }>();
	const pageIndex = location.state?.pageIndex || page;
	const { token } = useAuth0();
	const { formatDate } = useCompanyDateTime();
	const isRiskSetupDirty = selectIsRiskSetupDirty();
	const enablePreview = selectEnablePreview();
	const didMatrixUpdate = selectDidMatrixUpdate();

	const subscriptions = getSubscriptions();
	const hasViewerAccess = viewerAccess(subscriptions);

	const [forLoF, setForLoF] = useState(true);
	const [forInspection, setForInspection] = useState(true);
	const [cat, setCat] = useState<Category>();
	const [isNew, setNew] = useState(true);
	const [step2, setStep2] = useState<boolean>(false);
	const [component, setComponent] = useState<Component>({
		name: '',
		select: false,
		weighting: 0,
		type: '',
		metadata: undefined,
		metadataType: undefined,
		maxDistance: undefined,
		field: undefined,
		fieldAlias: undefined,
		nullScore: undefined,
	});
	const tab = selectRiskTab();
	const lofStore = selectLofStore();
	const cofStore = selectCofStore();
	const isLoFLoading =
		lofStore.isLoadingCategories || lofStore.isLoadingFields;
	const isCoFLoading =
		cofStore.isLoadingCategories || lofStore.isLoadingFields;
	const riskStore = selectRiskStore();
	const calcStatus = selectRiskStatus();
	const lastRun = selectRiskLastRun();
	const riskPage = selectRiskConfigsPage();
	const riskConfig = selectRiskConfig();
	const riskSetting = selectRiskSetting();
	const riskMatrixRanges = selectUpdatedRiskRanges();
	const riskMatrixGrid = selectUpdatedRiskMatrixGrid();
	const isRiskRunning = selectRiskRunning();
	const [openCancel, setOpenCancel] = useState(false);
	const [isRiskRunCancelling, setIsRiskRunCancelling] = useState(false);
	const overallRisk = selectOverallRisk();

	const [editName, setEditName] = useState(false);
	const [completeLoad, setCompleteLoad] = useState(false);

	//Feature flags
	const showRiskDetailsDatagrid = useIsFeatureEnabled(
		'info-360-render-risk-datagrid',
	);

	const multipleModels = useIsFeatureEnabled(
		'info-360-asset-multiple-risk-models',
	);

	const newRiskDesign = useIsFeatureEnabled(
		'info-360-asset-multiple-param-risk',
	);

	const websocketRiskStatus = useIsFeatureEnabled(
		'info-360-websocket-risk-status',
	);

	const websocketRiskScore = useIsFeatureEnabled(
		'info-360-websocket-risk-score',
	);

	/* eslint-disable-next-line */
	const updateStatusWebsocket = (data: any) => {
		// console.log('updateStatusWebsocket', data);
		const status = data.payload.calcStatus;
		const previousStatus = data.payload.previousStatus;
		updateStatus(status, previousStatus);
	};
	const updateStatus = (
		status: RiskCalcStatusResult,
		previousStatus?: RiskCalcStatusResult,
	) => {
		// console.log('updateStatusWebsocket', status, previousStatus);
		let result = '';
		let running = false;

		if (status) {
			switch (status) {
				case 'risk-calc-complete':
				case 'risk-calc-failed':
				case 'risk-calc-cancelled':
					result = t('Risk Service: Ready');

					// If we have changed state then display a snackbar the once
					if (
						previousStatus != undefined &&
						previousStatus != 'risk-calc-complete' &&
						previousStatus != 'risk-calc-failed' &&
						previousStatus != 'risk-calc-cancelled'
					) {
						if (status === 'risk-calc-complete') {
							dispatch(success(t('Risk calculation completed')));
							dispatch(setRunCompleteReload(true));
						} else if (status === 'risk-calc-cancelled') {
							dispatch(error(t('Risk calculation cancelled')));
						} else {
							dispatch(error(t('Risk calculation failed')));
						}
						dispatch(getLog(params.configId));
						dispatch(clearRiskScanId());
						dispatch(reloadRiskDetails(params.configId));
						dispatch(setRefreshList(true));

						// Do this in case another client has clicked cancel for your run
						setIsRiskRunCancelling(false);
					}
					break;
				case 'risk-calc-initiated':
				case 'risk-calc-start':
					result = t('Risk Service: In Progress');
					running = true;
					break;
				case 'risk-calc-getting-assets':
					result = t('Risk Service: Getting Assets');
					running = true;
					break;
				case 'risk-calc-getting-inspections':
					result = t('Risk Service: Getting Inspections');
					running = true;
					break;
				case 'risk-calc-getting-risk-config':
					result = t('Risk Service: Gettting Risk Configuration');
					running = true;
					break;
				case 'risk-calc-in-progress':
				case 'risk-calc-post-processing-normalize':
					result = t('Risk Service: In Progress');
					running = true;
					break;
				case 'risk-calc-writing-to-snowflake':
					result = t('Risk Service: Writing Results');
					running = true;
					break;
				case 'risk-calc-archiving-existing-in-snowflake':
					result = t('Risk Service: Archiving Results');
					running = true;
					break;
				// case 'error':
				// 	result = 'Risk Service: Unable to connect';
				// 	break;
			}
		}

		dispatch(setRiskStatus(result));
		dispatch(setRiskRunning(running));
		return true;
	};

	const pollRiskCalcStatus = async (cancel: boolean): Promise<void> => {
		if (cancel) await RiskCalcStatus.cancelPollForCalcStatus();
		else
			await RiskCalcStatus.pollForCalcStatus(
				token || '',
				params.configId,
				updateStatus,
			);
	};
	/* eslint-disable-next-line */
	const riskCalcStatusErrorCallBack = (data: any) => {
		console.log('riskCalcStatusErrorCallBack', data);
	};

	const webSocket = useWebSockets();
	useEffect(() => {
		// console.log('params.configId', params.configId);
		webSocket.setupRoutes(
			'riskCalcStatus',
			updateStatusWebsocket,
			riskCalcStatusErrorCallBack,
		);
		if (websocketRiskStatus)
			webSocket.sendMessage(
				JSON.stringify({
					path: 'risk',
					action: 'riskCalcStatus',
					configId: params.configId,
				}),
			);
	}, [params.configId]);

	useEffect(() => {
		// This is to stop it reloading everytime the tab changes
		if (riskStore.riskConfig.riskSetting.configId !== params.configId) {
			setCompleteLoad(true);
			dispatch(getRiskConfig(params.configId));
			if (!websocketRiskStatus) pollRiskCalcStatus(false);

			// If we have a new config ID and we aren't on the details tab switch to it
			if (tab != 'details')
				history.push(addRouteNamespace(`results/${params.configId}`));
		}
	}, []);

	useEffect(() => {
		if (!isRiskRunning) {
			setIsRiskRunCancelling(false);
		}
	}, [isRiskRunning]);

	const riskScoreCallBack = async (data: WebSocketData) => {
		// const key = data.payload.key;
		const response = data.payload;
		// const requestId = data.requestId;
		// const connectionId = data.connectionId;
		if (response.data && response.data.length > 0) {
			const resolvedResponse: ResolvedResponse = {
				pagination: response.pagination,
				data: response.data,
				scanId: response.scanId,
			};
			const rows = resolvedResponse.data as Row[];
			resolvedResponse.data = rows.map(riskDetail => {
				if (riskDetail.ASSET_ID.indexOf('#') > 0) {
					return {
						...riskDetail,
						ASSET_ID: riskDetail.ASSET_ID.split('#')[1],
					};
				} else {
					return riskDetail;
				}
			});
			dispatch(getRiskDetailsResolved(resolvedResponse));
		} else {
			dispatch(
				getRiskDetailsRejected({
					code: 500,
					text: 'Failed to get risk details',
				}),
			);
		}
		dispatch(setRiskDetailsIsBusy(false));
	};
	const riskScoreErrorCallBack = async (data: WebSocketData) => {
		dispatch(
			getRiskDetailsRejected({
				code: 500,
				text: 'Failed to get risk details',
			}),
		);
		dispatch(setRiskDetailsIsBusy(false));
	};

	const query = selectRiskDetailsQueryObj();

	const refreshRiskScore = () => {
		webSocket.setupRoutes(
			'riskScore',
			riskScoreCallBack,
			riskScoreErrorCallBack,
		);
		if (websocketRiskScore) {
			dispatch(setRiskDetailsIsBusy(true));
			dispatch(clearRiskScanId());
			webSocket.sendMessage(
				JSON.stringify({
					path: 'risk',
					action: 'riskScore',
					queryParameters: query,
				}),
			);
		} else {
			dispatch(getRiskDetailsDefaulted());
		}
	};

	useEffect(() => {
		// This so we dont try and load everything at once
		if (
			completeLoad &&
			riskStore.riskConfig.riskSetting.configId === params.configId
		) {
			setCompleteLoad(false);
			refreshRiskScore();
			dispatch(getLog(params.configId));
			dispatch(
				getLoFInspectionFields({
					assetType: riskStore.riskConfig.riskSetting.assetType,
					inspectionStandard:
						riskStore.riskConfig.riskSetting.inspectionStandard,
				}),
			);
			dispatch(getLoFFields(riskStore.riskConfig.riskSetting.assetType));
			dispatch(getCoFFields(riskStore.riskConfig.riskSetting.assetType));
			dispatch(getSimulationData());
			dispatch(getTaskData(riskStore.riskConfig.riskSetting.assetType));

			dispatch(getSpatialLayerList());
			dispatch(
				getProximityAttributeFields(
					riskStore.riskConfig.riskSetting.assetType,
				),
			);
			dispatch(
				getCustomTableFields(
					riskStore.riskConfig.riskSetting.assetType,
				),
			);
		}
	}, [completeLoad, riskStore]);

	const onAddComponent = (
		forLoF: boolean,
		forInspection: boolean,
		category: Category | undefined,
	) => {
		if ((forLoF && !isLoFLoading) || (!forLoF && !isCoFLoading)) {
			if (category) setCat(category);
			setForLoF(forLoF);
			setForInspection(forInspection);
			setNew(true);
			setComponent({
				name: '',
				select: false,
				weighting: 0,
				type: '',
				metadata: undefined,
				metadataType: undefined,
				maxDistance: undefined,
				field: undefined,
				fieldAlias: undefined,
				nullScore: undefined,
			});
			if (newRiskDesign) {
				setStep2(true);
			} else {
				dispatch(openDrawer('addNewComponentDrawer'));
			}
		}
	};

	const onEditComponent = (
		forLoF: boolean,
		forInspection: boolean,
		category: Category | undefined,
		component: Component,
	) => {
		if (category) setCat(category);
		setForInspection(forInspection);
		setForLoF(forLoF);
		setNew(false);
		setComponent(component);
		if (newRiskDesign) {
			setStep2(true);
		} else {
			dispatch(openDrawer('addNewComponentDrawer'));
		}
	};

	// TODO: Just hidden for the moment
	//const summaryTab = <div></div>;
	const riskDetailsTab = showRiskDetailsDatagrid ? (
		<RiskDetailsDatagrid
			configId={params.configId}
			refreshRiskScore={refreshRiskScore}
		/>
	) : (
		<RiskDetails configId={params.configId} />
	);

	const lofTab = (
		<LoFSetup
			onAddComponent={onAddComponent}
			onEditComponent={onEditComponent}
		/>
	);

	const cofTab = (
		<CoFSetup
			onAddComponent={onAddComponent}
			onEditComponent={onEditComponent}
		/>
	);

	const riskTab = <RiskSetup />;

	const logTab = <RiskLogs configId={params.configId} />;

	const refresh = () => {
		dispatch(getRiskConfig(params.configId));
	};

	const saveCLOFConfig = () => {
		if (tab === 'lof') {
			dispatch(saveLoF({ refresh: refresh }));
		} else if (tab === 'cof') {
			dispatch(saveCoF({ refresh: refresh }));
		}
		dispatch(setRefreshList(true));
	};

	const isConfigValid = () => {
		if (tab === 'lof') {
			const categories = lofStore.categories;
			if (!categories || categories.length === 0) return false;

			// For Risk Models with noConditionData they can have just the single fake Condition Category and still be valid
			const noCondition = noConditionData(
				riskStore.riskConfig.riskSetting.assetType,
			);
			if (noCondition && categories.length == 1) return true;

			const categoriesTotalWeight = categories
				.filter(category => !category.useThisOnly)
				.map(category => category.weighting)
				.reduce((prev, current) => prev + current, 0);
			if (
				categoriesTotalWeight !== 100 &&
				categories.filter(category => !category.useThisOnly).length > 0
			)
				return false;
			for (const category of categories) {
				if (
					!category.useHighestScore &&
					category.weighting !== 0 &&
					category.components &&
					category.components.length != 0
				) {
					// Total of 0 or 100 from active componments is valid
					const total = category.components
						.map(component =>
							component.select ? component.weighting : 0,
						)
						.reduce((prev, current) => prev + current, 0);
					if (total !== 0 && total !== 100) return false;
				}
			}

			return true;
		} else if (tab === 'cof') {
			const categories = cofStore.categories;
			if (!categories) return false;
			const categoriesTotalWeight = categories
				.map(category => category.weighting)
				.reduce((prev, current) => prev + current, 0);
			if (categoriesTotalWeight !== 100 && categories.length > 0)
				return false;
			for (const category of categories) {
				if (
					!category.useHighestScore &&
					category.weighting !== 0 &&
					category.components &&
					category.components.length != 0
				) {
					// Total of 0 or 100 from active componments is valid
					const total = category.components
						.map(component =>
							component.select ? component.weighting : 0,
						)
						.reduce((prev, current) => prev + current, 0);
					if (total !== 0 && total !== 100) return false;
				}
			}

			return true;
		}
	};

	const canBeSaved = () => {
		if (tab === 'lof') {
			return lofStore.isChanged && isConfigValid();
		} else if (tab === 'cof') {
			return cofStore.isChanged && isConfigValid();
		}

		return false;
	};

	const getLastRun = (lastRun: string) => {
		if (lastRun != undefined && lastRun != '') {
			return t('Last Run was {{date}}', {
				date: formatDateTimeWithFormatDate(lastRun, formatDate),
			});
		} else return '';
	};

	useEffect(() => {
		dispatch(
			setRiskLastRun(
				getLastRun(riskStore?.riskConfig.riskSetting.LastRun),
			),
		);
	}, [riskStore?.riskConfig.riskSetting.LastRun]);

	const onPreviewClicked = () => {
		if (riskSetting.matrix) {
			const previewObjPyld = {
				cofWeighting: overallRisk.cofWeighting,
				lofWeighting: overallRisk.lofWeighting,
				riskMethod: overallRisk.riskMethod,
				grades: riskMatrixRanges,
				grid: riskMatrixGrid,
				preview: {
					grades: riskMatrixRanges,
					grid: riskMatrixGrid,
				},
				configId: riskSetting.configId,
				initialLoad: true,
			};
			dispatch(initRiskMatrixPreview(previewObjPyld));
		}
	};

	const onPreviewCancelClicked = () => {
		if (riskSetting.matrix) {
			const matrix = {
				grades: riskMatrixRanges,
				grid: riskMatrixGrid,
				preview: {
					grades: riskMatrixRanges,
					grid: riskMatrixGrid,
				},
			};
			dispatch(
				cancelRiskMatrixPreview({
					configId: riskSetting.configId,
					matrix,
				}),
			);
		}
	};

	const onRunClicked = () => {
		dispatch(runRisk(params.configId));
		dispatch(setRefreshList(true));
	};

	const onCancelClicked = () => {
		setIsRiskRunCancelling(true);
		setOpenCancel(true);
	};

	// This stops the dialog closing when clicked on the background
	const onCloseDialog = () => void {};

	const dialogProps: DialogProps = {
		open: openCancel,
		onClose: onCloseDialog,
	};

	const handleCancelConfirmYes = () => {
		dispatch(resetRisk(params.configId));
		setOpenCancel(false);
	};

	const handleCancelConfirmNo = () => {
		setIsRiskRunCancelling(false);
		setOpenCancel(false);
	};

	const onSaveRiskClicked = () => {
		if (didMatrixUpdate) {
			//if Risk Matrix was updated
			const oldRiskConfig = { ...riskConfig };
			const oldRiskSetting = { ...riskSetting };
			const newMatrix = {
				grades: { ...riskStore.riskMatrix.grades },
				grid: [...riskStore.riskMatrix.grid],
			};
			const newSetting = { ...oldRiskSetting, matrix: newMatrix };
			const newRiskConfig: RiskConfig = {
				...oldRiskConfig,
				riskSetting: newSetting,
			};
			dispatch(saveRiskConfigAction(newRiskConfig));
		}
		dispatch(setOnlyGradesUpdated(false));
		dispatch(setDidMatrixUpdate(false));
		dispatch(setRiskSetupDirty(false));
		dispatch(setMatrixPreviewStatus(''));
		dispatch(setSaveRun(true));
		dispatch(setRefreshList(true));
	};

	const onEditName = () => {
		setEditName(true);
	};

	const onEditNameClose = (saved: boolean) => {
		setEditName(false);
		if (saved) {
			dispatch(changeRiskConfigsPage(riskPage.page));
		}
	};

	const getActionButton = () => {
		if (
			enablePreview &&
			!riskStore.isOnlyGradesUpdated &&
			!riskStore.isPreviewLoading
		) {
			return (
				<Button
					key="button-preview"
					dataCy="button-preview"
					disabled={riskStore.isLoading}
					variant="contained"
					color="primary"
					style={{
						height: '36px',
						marginLeft: '12px',
						width: '100%',
					}}
					onClick={onPreviewClicked}>
					{t('Preview')}
				</Button>
			);
		} else if (riskStore.isPreviewLoading) {
			return (
				<Button
					key="button-cancel-preview"
					dataCy="button-cancel-preview"
					variant="contained"
					color="error"
					style={{
						height: '36px',
						marginLeft: '12px',
						width: '100%',
					}}
					onClick={onPreviewCancelClicked}>
					{t('Cancel Preview')}
				</Button>
			);
		} else if (isRiskSetupDirty) {
			return (
				<Button
					key="button-save"
					dataCy="button-save"
					disabled={riskStore.isLoading}
					variant="contained"
					color="primary"
					style={{
						height: '36px',
						marginLeft: '12px',
						width: '100%',
					}}
					onClick={onSaveRiskClicked}>
					{t('Save')}
				</Button>
			);
		} else if (isRiskRunning) {
			return (
				<Button
					key="button-cancel"
					dataCy="button-cancel"
					variant="contained"
					color="error"
					disabled={isRiskRunCancelling}
					style={{
						height: '36px',
						marginLeft: '12px',
						width: '100%',
					}}
					onClick={onCancelClicked}>
					{t('Cancel Run')}
				</Button>
			);
		} else {
			return (
				<Button
					key="button-run"
					dataCy="button-run"
					disabled={riskStore.isLoading}
					variant="contained"
					color="primary"
					style={{
						height: '36px',
						marginLeft: '12px',
						width: '100%',
					}}
					onClick={onRunClicked}>
					{t('Run')}
				</Button>
			);
		}
	};

	const getSecondary = () => {
		if (tab === 'lof' || tab === 'cof') {
			return (
				<Grid container width="350px" style={{ textAlign: 'end' }}>
					<Grid item xs={7} style={{ alignSelf: 'center' }}>
						<Text variant="caption-small">{lastRun}</Text>
						<Text>{calcStatus}</Text>
					</Grid>
					<Grid item xs={5}>
						<Button
							disabled={!canBeSaved()}
							variant="contained"
							color="primary"
							style={{
								height: '36px',
								marginLeft: '12px',
								width: '100%',
							}}
							onClick={saveCLOFConfig}>
							{t('Save')}
						</Button>
					</Grid>
				</Grid>
			);
		} else if (tab === 'risk') {
			return (
				<Grid container width="350px" style={{ textAlign: 'end' }}>
					<Grid item xs={7} style={{ alignSelf: 'center' }}>
						<Text variant="caption-small">{lastRun}</Text>
						<Text>{calcStatus}</Text>
					</Grid>
					<Grid item xs={5}>
						{getActionButton()}
					</Grid>
				</Grid>
			);
		} else {
			return (
				<Grid container width="350px" style={{ textAlign: 'end' }}>
					<Grid item xs={12} style={{ alignSelf: 'center' }}>
						<Text variant="caption-small">{lastRun}</Text>
						<Text>{calcStatus}</Text>
					</Grid>
				</Grid>
			);
		}
	};

	const beforeTabChange = (event: CancellableEvent): CancellableEvent => {
		event.cancelled = true;
		switch (event.toIndex) {
			case 0:
				history.push(addRouteNamespace(`results/${params.configId}`));
				break;
			case 1:
				history.push(addRouteNamespace(`lofSetup/${params.configId}`));
				break;
			case 2:
				history.push(addRouteNamespace(`cofSetup/${params.configId}`));
				break;
			case 3:
				history.push(addRouteNamespace(`riskSetup/${params.configId}`));
				break;
			case 4:
				history.push(addRouteNamespace(`riskLog/${params.configId}`));
				break;
		}
		return event;
	};

	const back = () => {
		if (!websocketRiskStatus) pollRiskCalcStatus(true);
		history.push('/risk/');
	};

	const isLoaded = riskStore.riskConfig.riskSetting.name.length != 0;

	const fullRiskPage = (
		<FullPageWrapper
			title={
				riskStore.riskConfig.riskSetting.name.length == 0
					? ' '
					: riskStore.riskConfig.riskSetting.name
			}
			minorHeader={
				!hasViewerAccess && isLoaded ? (
					<div
						style={{
							paddingLeft: '20px',
							paddingRight: '20px',
						}}>
						<IconButton
							title={t('Edit Name')}
							data-cy="editButton"
							onClick={onEditName}>
							<Create />
						</IconButton>
					</div>
				) : (
					<></>
				)
			}
			applyPadding={true}
			headerUnderline
			secondary={
				riskStore.riskConfig.riskSetting ? getSecondary() : <></>
			}
			back={
				multipleModels
					? {
							action: back,
							label: t('Back to Risk Models'),
					  }
					: undefined
			}
			secondaryNav={{
				items: [
					// TODO: Just hidden for the moment
					//{ content: summaryTab, title: 'Summary' },
					{ content: riskDetailsTab, title: t('Results') },
					{
						content: lofTab,
						title: t('Likelihood of Failure Setup'),
						disabled: !isLoaded || hasViewerAccess,
					},
					{
						content: cofTab,
						title: t('Consequence of Failure Setup'),
						disabled: !isLoaded || hasViewerAccess,
					},
					{
						content: riskTab,
						title: t('Risk Setup'),
						disabled: !isLoaded || hasViewerAccess,
					},
					{
						content: logTab,
						title: t('Log'),
						disabled: !isLoaded || hasViewerAccess,
					},
				],
				itemIndex: pageIndex,
				beforeItemIndexChanged: beforeTabChange,
			}}></FullPageWrapper>
	);

	return (
		<>
			<WarningDialog
				dialogProps={dialogProps}
				data-cy="cancel-dialog"
				open={openCancel}
				title={t('Cancel Risk Run?')}
				content={t('Are you sure you want to cancel the risk run?')}
				cancelText={t('No')}
				cancelButtonProps={{
					dataCy: 'cancel-cancel',
				}}
				confirmText={t('Cancel Run')}
				confirmButtonProps={{
					dataCy: 'cancel-confirm',
				}}
				onCancel={handleCancelConfirmNo}
				onConfirm={handleCancelConfirmYes}
			/>

			{newRiskDesign ? (
				step2 ? (
					<>
						<AddEditComponent
							countKey={
								isNew
									? 'NewConfig'
									: `${params.configId}-${component.type}-${
											forLoF ? 'lof' : 'cof'
									  }-${component.name}`
							}
							forLoF={forLoF}
							observations={
								forLoF &&
								riskStore.riskConfig.riskSetting
									.inspectionStandard ===
									InspectionStandardType.PACP
							}
							inspectionType={
								forInspection
									? riskStore.riskConfig.riskSetting
											.inspectionStandard
									: undefined
							}
							isNew={isNew}
							assetType={
								riskStore.riskConfig.riskSetting.assetType
							}
							category={cat}
							component={component}
							onApply={() => {
								setStep2(false);
							}}
							onCancel={() => {
								setStep2(false);
							}}
						/>
						<NavigationPrompt
							title={t('Add New Component')}
							content={t(
								'Navigating away from this page will cancel the new component. Do you want to continue to navigate away from this page?',
							)}
							cancelText={t('No')}
							confirmText={t('Yes')}
							when={true}
							shouldBlockReload={true}
							navigate={nextLocation => {
								history.push(nextLocation);
							}}
							shouldBlockNavigation={() => true}
						/>
					</>
				) : (
					fullRiskPage
				)
			) : (
				<>
					<AddNewComponent
						forLoF={forLoF}
						forInspection={forInspection}
						isNew={isNew}
						assetType={riskStore.riskConfig.riskSetting.assetType}
						category={cat}
						component={component}
					/>
					{fullRiskPage}
				</>
			)}

			<RenameRisk open={editName} onClose={onEditNameClose} />
		</>
	);
};

export default RiskManagement;
